import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Details, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import type { SurveyProcess as Process } from '../../../api/surveyProcess';
import { Routes } from '../../../enums/Routes';
import { useSurveyProcessQuery } from '../../../hooks/queries/surveyProcess/useSurveyProcessQuery';
import { roundHeading } from '../processView';
import { ProcessSteps } from './ProcessSteps';
import { ProcessNowCard, RoundCompleted } from './ProcessNowCard';
import { ProcessActivity } from './ProcessActivity';
import classes from './SurveyProcess.module.css';

/** The steps of the round and what happens now. A completed round folds them away, as the action plan is what counts */
const Round = ({ process, teamId }: { process: Process; teamId: number }) => {
  const { t } = useTranslation();
  const round = (
    <div className={classes.round} data-completed={process.completed}>
      <ProcessSteps process={process} />
      <ProcessNowCard process={process} teamId={teamId} />
    </div>
  );
  if (!process.completed) return round;
  return (
    <Details>
      <Details.Summary>{t('surveyProcess.completed.showRound')}</Details.Summary>
      <Details.Content>{round}</Details.Content>
    </Details>
  );
};

/** Where the newest survey of the team stands: what went out, where, and what happens next */
export const SurveyProcess = ({ teamId }: { teamId: number }) => {
  const { t, i18n } = useTranslation();
  const { data: process, isLoading, isError, isSuccess } = useSurveyProcessQuery(teamId);
  const heading = process ? roundHeading(process, i18n.language) : null;

  return (
    <section aria-labelledby="process-heading" className={classes.process}>
      <div className={classes.heading}>
        <Heading level={2} data-size="sm" id="process-heading">
          {t('surveyProcess.title')}
        </Heading>
        {heading && (
          <Paragraph data-size="sm" className={classes.subtle}>
            {t(heading.key, heading.values)}
          </Paragraph>
        )}
      </div>
      {isLoading && <Skeleton variant="rectangle" height="200px" />}
      {isError && <Paragraph data-size="sm">{t('surveyProcess.loadError')}</Paragraph>}
      {isSuccess && !process && (
        <div>
          <Heading level={3} data-size="xs">
            {t('surveyProcess.empty.title')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('surveyProcess.empty.text')}
          </Paragraph>
          <Button asChild variant="secondary" data-size="sm">
            <Link to={Routes.CreateSurvey}>{t('surveyProcess.empty.start')}</Link>
          </Button>
        </div>
      )}
      {process && (
        <>
          <RoundCompleted process={process} />
          <Round process={process} teamId={teamId} />
          <ProcessActivity activity={process.activity} teamId={teamId} />
        </>
      )}
    </section>
  );
};
